import React from 'react';
import { bool, func } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { ensureCurrentUser } from '../../util/data';
import { sendVerificationEmail } from '../../ducks/user.duck';
import {
  LayoutSideNavigation,
  LayoutWrapperMain,
  LayoutWrapperAccountSettingsSideNav,
  LayoutWrapperTopbar,
  LayoutWrapperFooter,
  Footer,
  Page,
  UserNav,
  UserNavCustomer,
} from '../../components';
import { ContactDetailsForm } from '../../forms';
import { TopbarContainer } from '../../containers';

import { isScrollingDisabled } from '../../ducks/UI.duck';
import {
  saveContactDetails,
  saveContactDetailsClear,
  resetPassword,
} from './ContactDetailsPage.duck';
import css from './ContactDetailsPage.module.css';

export const ContactDetailsPageComponent = props => {
  const {
    saveEmailError = null,
    saveFavouritesError = null,
    saveNhsNumberError = null,
    savePhoneNumberError = null,
    saveBirthdayError = null,
    saveContactDetailsInProgress,
    currentUser = null,
    contactDetailsChanged,
    onChange,
    scrollingDisabled,
    sendVerificationEmailInProgress,
    sendVerificationEmailError = null,
    onResendVerificationEmail,
    onSubmitContactDetails,
    onResetPassword,
    resetPasswordInProgress = false,
    resetPasswordError = null,
    intl,
  } = props;

  const user = ensureCurrentUser(currentUser);
  const privateData = user.attributes.profile.privateData || {};
  const currentEmail = user.attributes.email || '';
  const currentFavourites = privateData.favourites;
  const currentNhsNumber = privateData.nhsNumber;
  const currentPhoneNumber = privateData.phoneNumber;
  const currentBirthday = privateData.birthday || {};

  const userTypes = privateData.userTypes || [];
  const isUserProvider = userTypes.includes('communicationProfessional');
  const isUserDeaf = userTypes.includes('deafUser');
  const isDeveloper = userTypes.includes('developer');

  const contactInfoForm = user.id ? (
    <ContactDetailsForm
      className={css.form}
      initialValues={{
        email: currentEmail,
        favourites: currentFavourites,
        nhsNumber: currentNhsNumber,
        phoneNumber: currentPhoneNumber,
        birthday: new Date(
          `${currentBirthday.year}-${currentBirthday.month}-${currentBirthday.day}`
        ),
        isUserDeaf: isUserDeaf,
        isUserProvider: isUserProvider,
        isDeveloper: isDeveloper,
      }}
      saveEmailError={saveEmailError}
      saveFavouritesError={saveFavouritesError}
      saveNhsNumberError={saveNhsNumberError}
      savePhoneNumberError={savePhoneNumberError}
      saveBirthdayError={saveBirthdayError}
      currentUser={currentUser}
      onResendVerificationEmail={onResendVerificationEmail}
      onSubmit={values => {
        onSubmitContactDetails({
          ...values,
          currentEmail,
          currentFavourites,
          currentNhsNumber,
          currentPhoneNumber,
          currentBirthday,
        });
      }}
      onChange={onChange}
      inProgress={saveContactDetailsInProgress}
      ready={contactDetailsChanged}
      sendVerificationEmailInProgress={sendVerificationEmailInProgress}
      sendVerificationEmailError={sendVerificationEmailError}
      resetPasswordInProgress={resetPasswordInProgress}
      resetPasswordError={resetPasswordError}
    />
  ) : null;

  const userNavOption =
    user.id && isUserProvider ? (
      <UserNav selectedPageName="ContactDetailsPage" />
    ) : (
      <UserNavCustomer selectedPageName="ContactDetailsPage" />
    );

  const title = intl.formatMessage({ id: 'ContactDetailsPage.title' });

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSideNavigation>
        <LayoutWrapperTopbar>
          <TopbarContainer
            currentPage="ContactDetailsPage"
            desktopClassName={css.desktopTopbar}
            mobileClassName={css.mobileTopbar}
          />
          {userNavOption}
        </LayoutWrapperTopbar>
        <LayoutWrapperAccountSettingsSideNav
          currentTab="ContactDetailsPage"
          isUserDeaf={isUserDeaf}
        />
        <LayoutWrapperMain>
          <div className={css.content}>
            <h1 className={css.title}>
              <FormattedMessage id="ContactDetailsPage.heading" />
            </h1>
            {contactInfoForm}
          </div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSideNavigation>
    </Page>
  );
};

ContactDetailsPageComponent.propTypes = {
  saveEmailError: propTypes.error,
  saveFavouritesError: propTypes.error,
  saveNhsNumberError: propTypes.error,
  savePhoneNumberError: propTypes.error,
  saveBirthdayError: propTypes.error,
  saveContactDetailsInProgress: bool.isRequired,
  currentUser: propTypes.currentUser,
  contactDetailsChanged: bool.isRequired,
  onChange: func.isRequired,
  onSubmitContactDetails: func.isRequired,
  scrollingDisabled: bool.isRequired,
  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  onResendVerificationEmail: func.isRequired,
  resetPasswordInProgress: bool,
  resetPasswordError: propTypes.error,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  // Topbar needs user info.
  const { currentUser, sendVerificationEmailInProgress, sendVerificationEmailError } = state.user;
  const {
    saveEmailError,
    saveFavouritesError,
    saveNhsNumberError,
    savePhoneNumberError,
    saveBirthdayError,
    saveContactDetailsInProgress,
    contactDetailsChanged,
    resetPasswordInProgress,
    resetPasswordError,
  } = state.ContactDetailsPage;
  return {
    saveEmailError,
    saveFavouritesError,
    saveNhsNumberError,
    savePhoneNumberError,
    saveBirthdayError,
    saveContactDetailsInProgress,
    currentUser,
    contactDetailsChanged,
    scrollingDisabled: isScrollingDisabled(state),
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    resetPasswordInProgress,
    resetPasswordError,
  };
};

const mapDispatchToProps = dispatch => ({
  onChange: () => dispatch(saveContactDetailsClear()),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
  onSubmitContactDetails: values => dispatch(saveContactDetails(values)),
  onResetPassword: values => dispatch(resetPassword(values)),
});

const ContactDetailsPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(ContactDetailsPageComponent);

export default ContactDetailsPage;
